import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Trans } from 'react-i18next';
import { Paper, Grid, FormGroup, Typography, Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import BaseMatManModal from '@monorepo/shared/src/components/common/Modals/BaseMatManModal';
import BaseModalTitle from '@monorepo/shared/src/components/common/Modals/BaseModalTitle';
import { translate } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import { useModal } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/Modal';
import Config from '@monorepo/shared/src/config';
import { updateUserSetting } from '@monorepo/shared/src/api';
import useClassrooms from 'state/hooks/use-classrooms';
import useUserStore from '@monorepo/shared/src/graph/hooks/use-user';
import { getSetting } from 'graph/selectors/setting-selector';
import useAssignments from 'state/hooks/use-assignments';
import { Settings } from 'types';
import EditAssignmentsHeader from '../../EditAssignment/components/EditAssignmentsHeader';
import GroupDropdown from '../../Assignments/components/GroupDropdown';
import { getSortedGroups } from '../../Assignments/utils';
import { useRouteGuard } from 'state/hooks/use-route-guard';

const tAssignment = (key, options?) => translate('assignment', key, options);

const AssignLetterContainer: React.FC = () => {
  const [user, { userRefetch }] = useUserStore();

  const { letter: letterParam } = useParams();

  const [{ activeClassroom }] = useClassrooms();

  const [{ assignments }, { handleExit, setSelectedGroupsAndStudents, setAllGroups, setLetter }] =
    useAssignments();
  const { isRouteGuardModalOpen, handleLeaveWithoutSaving, handleContinueEditing, closeModal } =
    useRouteGuard();
  const [, { openModal }] = useModal();
  const classes = useStyles();

  const updateViewedAssignmentsSetting = async () => {
    await updateUserSetting('viewedAssignments', {
      viewedAssignments: true,
    });
    await userRefetch();
  };

  const goToPli = async () => {
    const { pliUrl } = Config;
    await updateViewedAssignmentsSetting();
    window.location.replace(pliUrl as string);
  };

  const closeAssignmentsModal = async () => {
    await updateViewedAssignmentsSetting();
    closeModal();
  };

  const handleCancelAndClose = () => {
    handleExit();
    handleLeaveWithoutSaving();
  };

  const displayRouteGuardModal = () => {
    openModal({
      Title: () => (
        <BaseModalTitle text={tAssignment('unsavedChanges')} onClose={handleContinueEditing} />
      ),
      body: (
        <BaseMatManModal
          leftButtonAction={handleCancelAndClose}
          rightButtonAction={handleContinueEditing}
          headerText={tAssignment('unsavedChangesHeader')}
          headerFontSize={'24px'}
          leftButtonText={tAssignment('cancelAndClose')}
          rightButtonText={tAssignment('backToAssignment')}
        >
          <Box>
            <Typography variant="subtitle1">{tAssignment('unsavedChangesBody')}</Typography>
          </Box>
        </BaseMatManModal>
      ),
    });
  };

  const displayModal = () => {
    openModal({
      Title: () => (
        <BaseModalTitle
          text={tAssignment('firstAssignmentHeader')}
          onClose={closeAssignmentsModal}
        />
      ),
      body: (
        <BaseMatManModal
          leftButtonAction={goToPli}
          rightButtonAction={closeAssignmentsModal}
          headerText={tAssignment('firstAssignmentSubHeader')}
          headerFontSize={'22px'}
          leftButtonText={tAssignment('manageGroupsPli')}
          rightButtonText={tAssignment('continueWithGroup')}
        >
          <Box>
            <Typography variant="subtitle1">
              <Trans>{tAssignment('firstAssignmentBodyText2')}</Trans>
            </Typography>
            <Typography variant="subtitle1" className={classes.paddingTop}>
              {tAssignment('firstAssignmentBodyText3')}
            </Typography>
          </Box>
        </BaseMatManModal>
      ),
    });
  };

  const renderGroups = () => {
    if (!activeClassroom?.groups) return;
    const sortedGroups = getSortedGroups(activeClassroom?.groups);
    return sortedGroups?.map((group) => <GroupDropdown key={group.id} group={group} />);
  };

  useEffect(() => {
    if (user?.me?.settings?.length) {
      const viewedAssignmentsSetting = getSetting(user?.me?.settings, Settings.VIEWED_ASSIGNMENTS);
      if (!viewedAssignmentsSetting) {
        displayModal();
      }
    }
  }, [user?.me?.settings]);

  useEffect(() => {
    if (activeClassroom?.groups?.length) {
      setAllGroups(activeClassroom?.groups);
    }
  }, [activeClassroom]);

  useEffect(() => {
    setSelectedGroupsAndStudents();
  }, [assignments]);

  useEffect(() => {
    if (isRouteGuardModalOpen) {
      displayRouteGuardModal();
    }
  }, [isRouteGuardModalOpen]);

  useEffect(() => {
    if (letterParam) setLetter(letterParam);
  }, [letterParam]);

  return (
    <Paper className={classes.container}>
      <Grid container spacing={0} alignItems="center" justifyContent="center">
        <Grid item xs={10}>
          <EditAssignmentsHeader name={activeClassroom?.name} />
        </Grid>
        <Grid item xs={10}>
          <FormGroup>{renderGroups()}</FormGroup>
        </Grid>
      </Grid>
    </Paper>
  );
};

const useStyles = makeStyles(() => ({
  container: {
    width: '80%',
    margin: 'auto',
    paddingBottom: 60,
    marginTop: 30,
    justifyContent: 'space-evenly',
  },
  paddingTop: {
    paddingTop: 10,
  },
}));

export default AssignLetterContainer;
